/** Single source of truth for URLs. No route path is ever written inline. */
export const ROUTES = {
  /** `Log_in_1` — the pre-login landing with the two buttons. */
  welcome: '/welcome',
  login: '/login',
  register: '/register',
  /** Target of the confirmation link; expects `?token=`. */
  confirmEmail: '/confirm-email',
  forgotPassword: '/forgot-password',
  /** Target of the reset link; expects `?token=`. */
  resetPassword: '/reset-password',
  home: '/',
  wallet: '/wallet',
  /**
   * One operation from the combined history. `kind` names which of the four
   * lists owns the row, because the four backends number their rows
   * independently — `42` is a withdrawal AND a payout AND a transfer.
   */
  activity: '/activity/:kind/:id',
  /** Choose how to top up. Crypto is the only live method. */
  deposit: '/deposit',
  /** The deposit address screen. `?network=trc20|bsc` selects the rail. */
  depositCrypto: '/deposit/crypto',
  /** `Top_Up_6` / `Top_Up_7` — one deposit's receipt. */
  depositReceipt: '/deposit/receipt/:id',
  /** `Top_Up_3` / `Top_Up_4` — the user's own id, to hand to a sender. */
  shareId: '/deposit/id',
  /** Choose how to send money out. Mirrors the deposit chooser. */
  withdraw: '/withdraw',
  /** USDT to an external address. `?network=trc20|bsc` selects the rail. */
  withdrawCrypto: '/withdraw/crypto',
  /** Roubles to a bank card. One provider, never shown. */
  withdrawCard: '/withdraw/card',
  /** USDT to another Novix customer. */
  transfer: '/transfer',
  /**
   * The card product. `/cards` is the LIST — a customer may hold several, and the
   * approved mockup has no list screen at all (docs/PLAN.md §8а calls for one).
   * Everything below hangs off one card by its Novix id.
   */
  cards: '/cards',
  cardsNew: '/cards/new',
  card: '/cards/:cardId',
  cardTopUp: '/cards/:cardId/top-up',
  cardInfo: '/cards/:cardId/details',
  cardOtp: '/cards/:cardId/otp',
  cardClose: '/cards/:cardId/close',
  payouts: '/payouts',
  profile: '/profile',
  /** "Account safety" — the mockup's Profile_2. */
  profileSecurity: '/profile/security',
  /**
   * The transfer contact book. Under `/profile` rather than `/contacts`, because
   * it is a setting of the account rather than a destination of its own — the
   * tab bar has five slots and none of them is this.
   */
  profileContacts: '/profile/contacts',
  /**
   * The saved-card book. NOT `/cards`, which is the tab-bar route for the
   * (still placeholder) card product — a payout address book and a card product
   * are different things and must not share a URL.
   */
  profileCards: '/profile/cards'
} as const;

export type RoutePath = (typeof ROUTES)[keyof typeof ROUTES];

/**
 * The complete public surface. Everything not listed here needs a session.
 *
 * Written as a list rather than derived from the router so that adding a screen
 * cannot quietly make it public: a new route is protected by construction, and
 * opening it up means editing this line and being seen to do it.
 */
export const PUBLIC_ROUTES: readonly string[] = [
  ROUTES.welcome,
  ROUTES.login,
  ROUTES.register,
  ROUTES.confirmEmail,
  ROUTES.forgotPassword,
  ROUTES.resetPassword
];

/**
 * Validates a "send me back here after login" target.
 *
 * The value travels in router history state, which a page the user was on could
 * in principle influence, so it is treated as untrusted: only a same-origin
 * absolute path is accepted. `//evil.example` is a protocol-relative URL that
 * `<Navigate>` would happily follow off-site, which is why the second test is
 * not redundant.
 */
export function isInternalPath(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.startsWith('/') &&
    !value.startsWith('//') &&
    !value.startsWith('/\\')
  );
}

/** The post-login destination carried in history state, or the home screen. */
export function redirectTargetFrom(state: unknown): string {
  const from = (state as { from?: unknown } | null)?.from;
  return isInternalPath(from) ? from : ROUTES.home;
}

/**
 * `/activity/:kind/:id` with both parts filled in.
 *
 * `kind` is a closed vocabulary and never needs escaping, but the id does: three
 * of the four sources hand out ids the client only knows as strings, and this
 * path is shared into messengers.
 */
export function activityPath(kind: string, id: string): string {
  return `/activity/${kind}/${encodeURIComponent(id)}`;
}

/** `/deposit/receipt/:id` with the id filled in. Never build this path inline. */
export function depositReceiptPath(id: string): string {
  return `/deposit/receipt/${encodeURIComponent(id)}`;
}

/**
 * The card screens for one card.
 *
 * `cardId` is Novix's own id — the only card identifier that exists on the API
 * surface. Encoded on the way in, like every other path parameter.
 *
 * Nothing sensitive is ever a path or query parameter here. A PAN, a CVV or a
 * one-time code in a URL would land in history, in the referrer of any request the
 * page makes, and in every server log along the way.
 */
export function cardPath(cardId: string): string {
  return `/cards/${encodeURIComponent(cardId)}`;
}

export function cardTopUpPath(cardId: string): string {
  return `${cardPath(cardId)}/top-up`;
}

export function cardInfoPath(cardId: string): string {
  return `${cardPath(cardId)}/details`;
}

export function cardOtpPath(cardId: string): string {
  return `${cardPath(cardId)}/otp`;
}

export function cardClosePath(cardId: string): string {
  return `${cardPath(cardId)}/close`;
}
